import React, { useState, useEffect } from 'react';
import {
   Container,
   Box,
   Typography,
   Paper,
   Tabs,
   Tab,
   Divider,
   CircularProgress,
   Alert,
   Snackbar,
} from '@mui/material';
import {
   AutoFixHigh as AIIcon,
   PhotoLibrary as GalleryIcon,
   Analytics as AnalyticsIcon,
} from '@mui/icons-material';
import { useAuth } from '../../../contexts/AuthContext';
import PhotoSelector from './components/PhotoSelector';
import TransformationPanel from './components/TransformationPanel';
import TransformedGallery from './components/TransformedGallery';
import AnalyticsPanel from './components/AnalyticsPanel';
import { SelectedPhoto, TransformedPhoto } from './types';
import { useImageTransform } from '../../../hooks/useImageGen';
import { doc, collection, setDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import MyTransformedGallery from './components/MyTransformedGallery';
import { convertToJPEG } from '../../../utils/imageUtils';
import { getDownloadURL, uploadBytes } from 'firebase/storage';
import { ref } from 'firebase/storage';
import { getStorage } from 'firebase/storage';

// Tab panel component for handling tab content
interface TabPanelProps {
   children?: React.ReactNode;
   index: number;
   value: number;
}

function TabPanel(props: TabPanelProps) {
   const { children, value, index, ...other } = props;

   return (
      <div
         role='tabpanel'
         hidden={value !== index}
         id={`photo-studio-tabpanel-${index}`}
         aria-labelledby={`photo-studio-tab-${index}`}
         {...other}
         style={{ width: '100%' }}
      >
         {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
      </div>
   );
}

const AIPhotoStudioPage: React.FC = () => {
   // State management
   const [tabIndex, setTabIndex] = useState(0);
   const [selectedPhotos, setSelectedPhotos] = useState<SelectedPhoto[]>([]);
   const [transformedPhotos, setTransformedPhotos] = useState<any[]>([]);
   const [isTransforming, setIsTransforming] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const { currentUser } = useAuth();
   const [snackbarOpen, setSnackbarOpen] = useState(false);
   const [snackbarMessage, setSnackbarMessage] = useState('');
   const [snackbarSeverity, setSnackbarSeverity] = useState<'error' | 'info'>('info');
   const { imageToImageEditing, replaceBackground, crispUpscale, removeBackground, generateImage } =
      useImageTransform();

   const handleCloseSnackbar = () => {
      setSnackbarOpen(false);
   };

   // Handle tab change
   const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
      setTabIndex(newValue);
   };

   // Load previously transformed photos
   useEffect(() => {
      if (currentUser) {
         // Here we would fetch previously transformed photos from Firestore
         // For now, this is just a placeholder
      }
   }, [currentUser]);

   // Handle photo transformation
   const handleTransformPhotos = async (options: {
      stylePreset: string;
      subStyle: string;
      prompt?: string;
      strength?: number | null;
      transformationType?: string | null;
   }) => {
      const { stylePreset, subStyle, prompt, strength, transformationType } = options;
      if (selectedPhotos.length === 0) {
         setSnackbarMessage('Please select at least one photo to transform');
         setSnackbarSeverity('info');
         setSnackbarOpen(true);
         return;
      }

      setIsTransforming(true);
      setError(null);

      try {
         let transformedPhotos: any = null;

         if (transformationType === 'transform_style') {
            transformedPhotos = await imageToImageEditing({
               style: stylePreset,
               substyle: subStyle,
               prompt: prompt || '',
               strength: strength || 0,
               url: selectedPhotos[0].url,
            });
         } else if (transformationType === 'replace_background') {
            transformedPhotos = await replaceBackground({
               style: stylePreset,
               substyle: subStyle,
               prompt: prompt || '',
               url: selectedPhotos[0].url,
            });
         } else if (transformationType === 'remove_background') {
            transformedPhotos = await removeBackground(selectedPhotos[0].url);
         } else if (transformationType === 'generate_image') {
            transformedPhotos = await generateImage({
               prompt: prompt || '',
               style: stylePreset || '',
               substyle: subStyle || '',
            });
         } else if (transformationType === 'crisp') {
            transformedPhotos = await crispUpscale(selectedPhotos[0].url);
         }

         if (transformedPhotos?.images) {
            setTransformedPhotos((prev) => [
               ...transformedPhotos?.images?.map((photo: any) => ({
                  id: photo?.image_id,
                  originalPhotoId: selectedPhotos[0].id,
                  transformedUrl: photo?.url,
                  thumbnailUrl: photo?.url,
                  stylePreset: stylePreset,
                  prompt: prompt || '',
                  createdAt: new Date(transformedPhotos?.created),
                  type: selectedPhotos[0]?.type,
                  tripId: selectedPhotos[0]?.tripId || null,
                  eventId: selectedPhotos[0]?.eventId || null,
                  userId: selectedPhotos[0]?.userId || null,
               })),
            ]);
         }
      } catch (err) {
         setError(
            `An error occurred while transforming photos: ${err instanceof Error ? err.message : 'Unknown error'}`
         );
      } finally {
         setIsTransforming(false);
      }
   };

   const handleEnhanceImage = async (images: any[], photoId: any) => {
      try {
         setTransformedPhotos((prev) => [
            ...images?.map((photo: any) => ({
               id: photo?.image_id,
               originalPhotoId: photoId,
               transformedUrl: photo?.url,
               thumbnailUrl: photo?.url,
               stylePreset: '',
               prompt: '',
               createdAt: new Date(),
               type: selectedPhotos[0]?.type,
               tripId: selectedPhotos[0]?.tripId || null,
               eventId: selectedPhotos[0]?.eventId || null,
               userId: selectedPhotos[0]?.userId || null,
            })),
         ]);
      } catch (err) {}
   };

   const saveTransformedPhoto = async (photo: TransformedPhoto) => {
      try {
         if (photo.type === 'trip') {
            const { blob, extension } = await convertToJPEG(photo?.transformedUrl);

            const path = `transformed_photos/${currentUser?.uid}/${photo.id}.${extension}`;

            const storage = getStorage();
            const storageRef = ref(storage, path);

            await uploadBytes(storageRef, blob);

            const photoUrl = await getDownloadURL(storageRef);

            await setDoc(doc(collection(db, 'photos')), {
               tripId: photo.tripId || null,
               photoId: photo.id,
               userId: photo.userId || null,
               type: 'transformed_photo',
               createdAt: new Date(),
               url: photoUrl,
               isTransformed: true,
               basePhotoId: photo.id,
            });
            setSnackbarMessage('Photo saved successfully');
            setSnackbarSeverity('info');
            setSnackbarOpen(true);
         } else if (photo.type === 'event') {
            const { blob, extension } = await convertToJPEG(photo?.transformedUrl);

            const path = `transformed_photos/${currentUser?.uid}/${photo.id}.${extension}`;

            const storage = getStorage();
            const storageRef = ref(storage, path);

            await uploadBytes(storageRef, blob);

            const photoUrl = await getDownloadURL(storageRef);

            await setDoc(doc(collection(db, 'photos')), {
               eventId: photo.eventId || null,
               photoId: photo.id,
               userId: photo.userId || null,
               type: 'transformed_photo',
               isTransformed: true,
               createdAt: new Date(),
               url: photoUrl,
               basePhotoId: photo.id,
            });
            setSnackbarMessage('Photo saved successfully');
            setSnackbarSeverity('info');
            setSnackbarOpen(true);
         } else if (photo.type === 'user_photo') {
            const { blob, extension } = await convertToJPEG(photo?.transformedUrl);

            const path = `transformed_photos/${currentUser?.uid}/${photo.id}.${extension}`;

            const storage = getStorage();
            const storageRef = ref(storage, path);

            await uploadBytes(storageRef, blob);

            const photoUrl = await getDownloadURL(storageRef);

            await setDoc(doc(collection(db, 'photos')), {
               userId: photo.userId || null,
               type: 'transformed_photo',
               baseType: 'user_photo',
               createdAt: new Date(),
               url: photoUrl,
               isTransformed: true,
               basePhotoId: photo.id,
            });
            setSnackbarMessage('Photo saved successfully');
            setSnackbarSeverity('info');
            setSnackbarOpen(true);
         }
      } catch (err) {
         setSnackbarMessage('Error saving photo');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      }
   };

   return (
      <Box sx={{ width: '100%', px: { xs: 1, sm: 2 }, mt: 2, mb: 4 }}>
         <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
            <Typography variant='h4' component='h1' gutterBottom>
               <AIIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
               AI Photo Studio
            </Typography>
            <Typography variant='body1' color='text.secondary' paragraph>
               Transform your photos with AI, share them on social media, and track their
               performance.
            </Typography>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
               <Tabs
                  value={tabIndex}
                  onChange={handleTabChange}
                  aria-label='photo studio tabs'
                  variant='fullWidth'
               >
                  <Tab
                     icon={<AIIcon />}
                     label='Transform Photos'
                     id='photo-studio-tab-0'
                     aria-controls='photo-studio-tabpanel-0'
                  />
                  <Tab
                     icon={<GalleryIcon />}
                     label='My Transformations'
                     id='photo-studio-tab-1'
                     aria-controls='photo-studio-tabpanel-1'
                  />
                  {/* <Tab
                     icon={<AnalyticsIcon />}
                     label='Analytics'
                     id='photo-studio-tab-2'
                     aria-controls='photo-studio-tabpanel-2'
                  /> */}
               </Tabs>
            </Box>
         </Paper>

         {error && (
            <Alert severity='error' sx={{ mb: 3 }}>
               {error}
            </Alert>
         )}

         <TabPanel value={tabIndex} index={0}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
               <Box sx={{ width: { xs: '100%', md: '40%' } }}>
                  <PhotoSelector
                     onPhotoSelect={(photos) => {
                        if (photos.length > 0) {
                           setSelectedPhotos(photos);
                        } else {
                           setSelectedPhotos([]);
                           setTransformedPhotos([]);
                        }
                     }}
                     selectedPhotos={selectedPhotos}
                  />
               </Box>

               <Divider
                  orientation='vertical'
                  flexItem
                  sx={{ display: { xs: 'none', md: 'block' } }}
               />

               <Box sx={{ width: { xs: '100%', md: '60%' } }}>
                  <TransformationPanel
                     selectedPhotos={selectedPhotos}
                     onTransform={handleTransformPhotos}
                     isTransforming={isTransforming}
                     onEnhanceImage={handleEnhanceImage}
                  />

                  {transformedPhotos.length > 0 && (
                     <Box sx={{ mt: 4 }}>
                        <Typography variant='h6' gutterBottom>
                           Recent Transformations
                        </Typography>
                        <TransformedGallery
                           savePhoto={saveTransformedPhoto}
                           originalPhoto={selectedPhotos[0]}
                           photos={transformedPhotos}
                           onViewAll={() => setTabIndex(1)}
                        />
                     </Box>
                  )}
               </Box>
            </Box>
         </TabPanel>

         <TabPanel value={tabIndex} index={1}>
            <Typography variant='h6' gutterBottom>
               My Transformed Photos
            </Typography>
            <MyTransformedGallery savePhoto={saveTransformedPhoto} />
         </TabPanel>

         <TabPanel value={tabIndex} index={2}>
            <AnalyticsPanel />
         </TabPanel>

         <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
         >
            <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
               {snackbarMessage}
            </Alert>
         </Snackbar>
      </Box>
   );
};

export default AIPhotoStudioPage;
